import React, { useEffect, useRef } from 'react';

const CAPTCHA_SCRIPT_URL = 'https://smartcaptcha.yandexcloud.net/captcha.js';

declare global {
  interface Window {
    smartCaptcha?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
        }
      ) => number;
      reset?: (widgetId: number) => void;
    };
  }
}

export interface SmartCaptchaProps {
  siteKey: string;
  onSuccess: (token: string) => void;
  className?: string;
  style?: React.CSSProperties;
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.smartCaptcha) {
    return Promise.resolve();
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CAPTCHA_SCRIPT_URL;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load SmartCaptcha script'));

    document.head.appendChild(script);
  });

  return scriptPromise;
}

const SmartCaptchaComponent: React.FC<SmartCaptchaProps> = (props: SmartCaptchaProps) => {
  const { siteKey, onSuccess, className, style = { minHeight: 100 } } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    let cancelled = false;

    const cleanup = () => {
      if (widgetIdRef.current !== null && window.smartCaptcha?.reset) {
        try {
          window.smartCaptcha.reset(widgetIdRef.current);
        } catch {
          // ignore
        }
      }
      widgetIdRef.current = null;

      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };

    if (!siteKey || !containerRef.current) {
      return () => cleanup();
    }

    const container = containerRef.current;

    loadScript()
      .then(() => {
        if (cancelled || !window.smartCaptcha) return;

        widgetIdRef.current = window.smartCaptcha.render(container, {
          sitekey: siteKey,
          callback: (token: string) => {
            if (!cancelled) onSuccessRef.current(token);
          },
        });
      })
      .catch(() => {
        // optional: логирование
      });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [siteKey]);

  return <div ref={containerRef} className={className} style={style} />;
};

export const SmartCaptcha = React.memo(SmartCaptchaComponent);
